'use client';

import { useEffect, useRef, useState } from 'react';
import { cn } from '@/core/utils/cn';
import { ResponsiveImage, type ImageLoadPriority } from '../shared/ResponsiveImage';

interface ResponsiveVideoProps {
  desktopSrc?: string;
  mobileSrc?: string;
  desktopPoster?: string;
  mobilePoster?: string;
  posterAlt?: string;
  posterPriority?: ImageLoadPriority;
  posterPreloadMedia?: string;
  sizes?: string;
  className?: string;
  objectPosition?: string;
  objectFit?: 'cover' | 'contain';
  active?: boolean;
  loop?: boolean;
  onEnded?: () => void;
  // Admin canvas simulates viewport by scaling a real-width div. This selects the preview source
  // explicitly instead of consulting the actual browser window.
  forceMobile?: boolean;
}

export function ResponsiveVideo({
  desktopSrc = '',
  mobileSrc = '',
  desktopPoster = '',
  mobilePoster = '',
  posterAlt = '',
  posterPriority = 'none',
  posterPreloadMedia,
  sizes = '100vw',
  className,
  objectPosition,
  objectFit,
  active = true,
  loop = true,
  onEnded,
  forceMobile,
}: ResponsiveVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasStarted, setHasStarted] = useState(false);
  // A video downloads and plays only near the screen: one further down the page (a row of reels)
  // or hidden on this breakpoint (display:none never intersects) costs nothing until it is seen, and
  // the server HTML no longer carries autoplay, so a hero video doesn't compete with the LCP poster.
  const [inView, setInView] = useState(false);
  // Visitors who asked for less motion keep the poster (or the first frame) instead of a loop.
  const [reducedMotion, setReducedMotion] = useState(false);
  const fit = objectFit || 'cover';
  const position = objectPosition || 'center center';

  const forcedSrc = forceMobile === undefined ? '' : (forceMobile ? (mobileSrc || desktopSrc) : (desktopSrc || mobileSrc));
  const forcedPoster = forceMobile === undefined ? '' : (forceMobile ? (mobilePoster || desktopPoster) : (desktopPoster || mobilePoster));
  const onlyDesktopVideo = forceMobile === undefined && Boolean(desktopSrc) && !mobileSrc;
  const onlyMobileVideo = forceMobile === undefined && !desktopSrc && Boolean(mobileSrc);
  const visibilityClass = onlyDesktopVideo ? 'hidden sm:block' : onlyMobileVideo ? 'block sm:hidden' : '';

  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!query) return;
    setReducedMotion(query.matches);
    const onChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    query.addEventListener?.('change', onChange);
    return () => query.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { rootMargin: '200px 0px' });
    observer.observe(video);
    return () => observer.disconnect();
  }, [forcedSrc, desktopSrc, mobileSrc]);

  const playing = active && inView && !reducedMotion;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (!playing) {
      video.pause();
      return;
    }
    void video.play().catch(() => {
      // Muted inline autoplay is broadly supported; the poster remains visible if a browser or
      // visitor preference still blocks it.
    });
  }, [playing, forcedSrc, desktopSrc, mobileSrc]);

  const posterSrc = forcedPoster || desktopPoster || mobilePoster;
  const posterMobileSrc = forceMobile === undefined && mobilePoster ? mobilePoster : undefined;

  return (
    <>
      {posterSrc && !hasStarted && (
        <ResponsiveImage
          src={posterSrc}
          mobileSrc={posterMobileSrc}
          alt={posterAlt}
          sizes={sizes}
          priority={posterPriority}
          preloadMedia={posterPreloadMedia}
          className={cn(className, visibilityClass, 'z-[1] pointer-events-none')}
          style={{ objectFit: fit, objectPosition: position }}
        />
      )}
      <video
        ref={videoRef}
        key={forcedSrc || `${desktopSrc}|${mobileSrc}`}
        src={forcedSrc || undefined}
        poster={forcedPoster || undefined}
        autoPlay={playing}
        loop={loop}
        muted
        playsInline
        // Without a poster, a still visitor (reduced motion) gets the first frame rather than nothing.
        preload={playing || (inView && reducedMotion && !posterSrc) ? 'metadata' : 'none'}
        className={cn(className, visibilityClass)}
        style={{ objectFit: fit, objectPosition: position }}
        onPlaying={() => setHasStarted(true)}
        onEnded={onEnded}
      >
        {!forcedSrc && mobileSrc && <source media="(max-width: 639px)" src={mobileSrc} />}
        {!forcedSrc && desktopSrc && <source media="(min-width: 640px)" src={desktopSrc} />}
      </video>
    </>
  );
}
